import type { AgentSession } from 'src/pages/automation/agents/slice/agentApi';

import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

// ----------------------------------------------------------------------

export type AiAgentInboxMode = 'autopilot' | 'copilot' | 'off';

export interface AiAgentInboxStatus {
  agentId: string | null;
  agentName: string | null;
  mode: AiAgentInboxMode;
  isActive: boolean;
  assignedAt: string | null;
  sessionId: string | null;
  session?: AgentSession | null;
}

export interface AssignAiAgentRequest {
  conversationId: string;
  agentId: string;
  mode?: AiAgentInboxMode;
}

export interface DeactivateAiAgentRequest {
  conversationId: string;
}

// ----------------------------------------------------------------------

const AI_AGENT_INBOX_API = {
  assign: (conversationId: string) =>
    `api/ai-agents/conversations/${conversationId}/ai-agent/assign`,
  deactivate: (conversationId: string) =>
    `api/ai-agents/conversations/${conversationId}/ai-agent/deactivate`,
  updateMode: (conversationId: string) => `api/ai-agents/conversations/${conversationId}/ai-agent/mode`,
  status: (conversationId: string) =>
    `api/ai-agents/conversations/${conversationId}/ai-agent/status`,
  listActive: 'api/ai-agents',
};

// ----------------------------------------------------------------------

export const aiAgentInboxApi = createApi({
  reducerPath: 'aiAgentInboxApi',
  baseQuery,
  tagTypes: ['AiAgentInboxStatus'],
  endpoints: (builder) => ({
    assignAiAgent: builder.mutation<{ data: AiAgentInboxStatus }, AssignAiAgentRequest>({
      query: ({ conversationId, ...body }) => ({
        url: AI_AGENT_INBOX_API.assign(conversationId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'AiAgentInboxStatus', id: conversationId },
      ],
    }),

    updateAiAgentMode: builder.mutation<
      { data: AiAgentInboxStatus },
      {
        conversationId: string;
        aiAgentId?: string;
        aiAgentMode?: 'autopilot' | 'copilot' | 'off';
        isActive?: boolean;
      }
    >({
      query: ({ conversationId, aiAgentMode, isActive }) => ({
        url: AI_AGENT_INBOX_API.updateMode(conversationId),
        method: 'PATCH',
        // Map isActive toggle → mode string; explicit aiAgentMode takes precedence
        body: {
          mode: (aiAgentMode ?? (isActive ? 'autopilot' : 'off')).toLowerCase(),
        },
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'AiAgentInboxStatus', id: conversationId },
      ],
    }),

    deactivateAiAgent: builder.mutation<{ data: AiAgentInboxStatus }, DeactivateAiAgentRequest>({
      query: ({ conversationId }) => ({
        url: AI_AGENT_INBOX_API.deactivate(conversationId),
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'AiAgentInboxStatus', id: conversationId },
      ],
    }),

    getAiAgentStatus: builder.query<AiAgentInboxStatus, string>({
      query: (conversationId) => ({
        url: AI_AGENT_INBOX_API.status(conversationId),
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => {
        const d = response?.data;
        if (!d) return { agentId: null, agentName: null, mode: 'off' as AiAgentInboxMode, isActive: false, assignedAt: null, sessionId: null, session: null };
        const mode = (d.mode ?? 'off').toLowerCase() as AiAgentInboxMode;
        return { ...d, mode, isActive: mode !== 'off' };
      },
      providesTags: (_result, _error, conversationId) => [
        { type: 'AiAgentInboxStatus', id: conversationId },
      ],
    }),

    listActiveAgents: builder.query<
      {
        data: {
          data: Array<{
            id: string;
            name: string;
            description?: string;
            type: string;
            status: string;
            data: any;
          }>;
        };
        total: number;
      },
      void
    >({
      query: () => ({
        url: AI_AGENT_INBOX_API.listActive,
        params: { status: 'ACTIVE' },
      }),
    }),
  }),
});

export const {
  useAssignAiAgentMutation,
  useUpdateAiAgentModeMutation,
  useDeactivateAiAgentMutation,
  useGetAiAgentStatusQuery,
  useListActiveAgentsQuery,
} = aiAgentInboxApi;
